import { Link, useNavigate } from "@tanstack/react-router";
import { Clock, Download } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { SetupListItem } from "@/lib/types";

function formatLapTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const wholeSecs = Math.floor(secs);
  const ms = Math.round((secs - wholeSecs) * 1000);
  return `${mins}:${String(wholeSecs).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

interface SetupCardProps {
  setup: SetupListItem;
}

export function SetupCard({ setup }: SetupCardProps) {
  const navigate = useNavigate();
  const car = Array.isArray(setup.car) ? setup.car[0] : setup.car;
  const driver = Array.isArray(setup.driver) ? setup.driver[0] : setup.driver;

  const showVersion = (setup.latest_version?.version_number ?? 1) > 1;

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() =>
        navigate({
          to: "/app/setups/$setupId",
          params: { setupId: setup.id },
        })
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          navigate({
            to: "/app/setups/$setupId",
            params: { setupId: setup.id },
          });
        }
      }}
      className="group relative block cursor-pointer overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm transition-all hover:border-border hover:shadow-md"
    >
      {/* Kalocsai pattern — fills bottom content area, fades toward top */}
      <img
        src="/kalocsai-tile.webp"
        alt=""
        loading="lazy"
        className="pointer-events-none absolute bottom-0 left-0 z-10 w-full opacity-0 transition-opacity duration-500 group-hover:opacity-30"
        style={{
          height: "45%",
          objectFit: "cover",
          objectPosition: "center bottom",
          maskImage: "linear-gradient(to top, black 0%, transparent 60%)",
          WebkitMaskImage: "linear-gradient(to top, black 0%, transparent 60%)",
        }}
      />

      {/* Car image thumbnail */}
      <div className="relative aspect-[16/9] w-full overflow-hidden rounded-t-xl">
        {car?.image_url ? (
          <img
            src={car.image_url}
            alt={car.name}
            loading="lazy"
            width={400}
            height={225}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-[#1a2744] to-[#0d1b2a]" />
        )}

        {/* Download count overlay */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs text-white">
          <Download className="h-3 w-3" />
          {setup.download_count}
        </div>

        {/* Version indicator overlay */}
        {showVersion && (
          <div className="absolute top-2 right-2 rounded-md bg-black/60 px-2 py-1 text-xs text-white">
            v{setup.latest_version?.version_number}
            {setup.latest_version?.change_notes && (
              <span className="ml-1 text-white/70">
                &mdash; {setup.latest_version.change_notes}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="relative z-20 p-3">
        {/* Title */}
        <h3 className="font-[Bebas_Neue] text-base leading-tight text-foreground group-hover:text-primary transition-colors line-clamp-1">
          {setup.title.replace(/^Week\s+\d+\s+/i, "").replace(/\s*\/\s*Week\s+\d+$/i, "")}
        </h3>

        {/* Series */}
        <p className="mt-1.5 text-xs text-muted-foreground line-clamp-1">
          {setup.series?.name ?? "Unknown Series"}
        </p>

        {/* Driver */}
        <p className="mt-1 text-xs">
          {driver ? (
            <Link
              to="/drivers/$driverId"
              params={{ driverId: driver.id }}
              className="text-primary/80 hover:text-primary hover:underline transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {driver.display_name}
            </Link>
          ) : (
            <span className="text-muted-foreground">Unknown Driver</span>
          )}
        </p>

        {/* Bottom row: timestamp + free badge + lap time */}
        <div className="mt-3 flex items-center justify-between gap-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground/70">
              {timeAgo(setup.updated_at ?? setup.created_at)}
            </span>
            {setup.is_free_sample && (
              <Badge className="bg-[#3B9B7A] border-transparent text-white text-[10px] px-1.5 py-0">
                Free
              </Badge>
            )}
          </div>
          {setup.latest_version?.best_lap_time != null && (
            <div className="flex items-center gap-1 shrink-0 text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span className="font-[Bebas_Neue] text-base leading-none text-foreground">
                {formatLapTime(Number(setup.latest_version.best_lap_time))}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
